import { action, makeObservable, observable } from 'mobx';
import { moveItem } from '@bringg-frontend/utils';
import { localLogger } from '@bringg-frontend/bringg-web-infra';

import { TableDefinition } from './table-definition';
import { TableDefinitionData } from 'bringg-web/features/monitoring/shared/stores/groups/types';

export class TableDefinitionStore {
	public tableDefinitions: Map<string, TableDefinition>;
	public readonly usageOrder: { key: string; lastUsed: Date }[];
	private static readonly CACHE_SIZE = 15;
	private static readonly MAX_RETENTION_TIME = 5 * 60 * 1000;
	private removeOldUsagesInterval: number;

	constructor() {
		this.tableDefinitions = new Map<string, TableDefinition>();
		this.usageOrder = [];

		makeObservable(this, {
			add: action,
			useExisting: action,
			usageOrder: observable,
			tableDefinitions: observable
		});
	}

	public useExisting(key: string) {
		localLogger.log(`table definition store - using existing key ${key}`);

		const keyIndex = this.usageOrder.findIndex(usage => usage.key === key);

		moveItem(this.usageOrder, keyIndex, 0);
		this.usageOrder[0].lastUsed = new Date();
	}

	public add(key: string, tableDefinitionData: TableDefinitionData) {
		localLogger.log(`table definition store - adding new key ${key}`);

		const newTableDefinition = new TableDefinition(
			tableDefinitionData.filters,
			tableDefinitionData.sorting,
			tableDefinitionData.groupMode,
			tableDefinitionData.entityBaseType
		);

		if (this.tableDefinitions.size === 0) {
			this.startRemoveOldUsagesInterval();
		}

		this.addUsage(key, newTableDefinition);

		if (this.tableDefinitions.size > TableDefinitionStore.CACHE_SIZE) {
			this.removeOldestUsage();
		}
	}

	public has(key: string) {
		return this.tableDefinitions.has(key);
	}

	public getLatestTableDefinition(owner: string) {
		const latestUsage = this.usageOrder.find(usage => {
			return JSON.parse(usage.key).owner === owner;
		});

		return this.tableDefinitions.get(latestUsage?.key);
	}

	public reset() {
		if (this.removeOldUsagesInterval) {
			clearInterval(this.removeOldUsagesInterval);
			this.removeOldUsagesInterval = undefined;
		}
	}

	private addUsage(key: string, tableDefinition: TableDefinition) {
		this.usageOrder.unshift({ key, lastUsed: new Date() });
		this.tableDefinitions.set(key, tableDefinition);
	}

	private removeOldestUsage() {
		const oldestUsage = this.usageOrder.pop();
		localLogger.log(`table definition store - removing oldest usage`, oldestUsage);
		this.tableDefinitions.delete(oldestUsage.key);
	}

	private startRemoveOldUsagesInterval() {
		localLogger.log(`table definition store - starting interval`);

		this.removeOldUsagesInterval = window.setInterval(() => {
			let hasOldUsage = true;
			localLogger.log(`table definition store - interval - removing old usages`);

			while (hasOldUsage && this.usageOrder.length > 1) {
				const oldestUsage = this.usageOrder[this.usageOrder.length - 1];

				if (new Date().valueOf() - oldestUsage.lastUsed.valueOf() > TableDefinitionStore.MAX_RETENTION_TIME) {
					this.removeOldestUsage();
				} else {
					hasOldUsage = false;
				}
			}
		}, 60000);
	}
}
